import React, { useState } from "react";
import { Link } from "react-router-dom";
import SminHeader from "@/components/SminHeader";
import SminFooter from "@/components/SminFooter";
import { MARKETING_NAV as NAV } from "@/lib/marketingNav";
import { Check, X, ArrowRight, ChevronDown } from "lucide-react";

const TIERS = [
  {
    name: "Starter", price: "$0", period: "forever",
    tagline: "For solo geologists evaluating the platform.",
    cta: "Start Free", ctaHref: "/signup",
    features: [
      "1 project",
      "Up to 100 000 drillhole intervals",
      "3D viewer + IDW estimation",
      "PDF report exports",
      "Community forum support",
    ],
  },
  {
    name: "Team", price: "$799", period: "/ month",
    tagline: "For consulting teams and small mining companies.",
    cta: "Request Demo", ctaHref: "/contact", highlight: true,
    features: [
      "10 projects · 5 users",
      "Up to 10 million drillhole intervals",
      "Ordinary Kriging + LG pit optimisation",
      "JORC / NI 43-101 report templates",
      "AI Assistant · 500 sessions / month",
      "Priority WhatsApp + email support",
    ],
  },
  {
    name: "Enterprise", price: "Custom", period: "annual",
    tagline: "For producers with multi-site portfolios & audit needs.",
    cta: "Contact Sales", ctaHref: "/contact",
    features: [
      "Unlimited projects · unlimited users",
      "500 million+ assay records",
      "SSO / SAML · role-based access",
      "Signed PDF audit trail",
      "Dedicated Slack channel + 24×7 SLA",
      "On-prem / air-gapped deployment option",
    ],
  },
];

const COMPARE = [
  { row: "Projects",                       starter: "1",       team: "10",           ent: "Unlimited" },
  { row: "Users",                          starter: "1",       team: "5",            ent: "Unlimited" },
  { row: "Drillhole intervals",            starter: "100k",    team: "10M",          ent: "500M+" },
  { row: "Block-model cells",              starter: "500k",    team: "10M",          ent: "Unlimited" },
  { row: "IDW estimation",                 starter: true,      team: true,           ent: true },
  { row: "Ordinary Kriging",               starter: false,     team: true,           ent: true },
  { row: "LG pit optimisation",            starter: false,     team: true,           ent: true },
  { row: "NPV scheduling",                 starter: false,     team: true,           ent: true },
  { row: "JORC / NI 43-101 templates",     starter: "PDF only", team: "All formats", ent: "All + custom" },
  { row: "AI Assistant",                   starter: "50 / mo", team: "500 / mo",     ent: "Unlimited" },
  { row: "SSO / SAML",                     starter: false,     team: false,          ent: true },
  { row: "Audit trail export",             starter: false,     team: true,           ent: true },
  { row: "On-prem / air-gapped",           starter: false,     team: false,          ent: true },
  { row: "Support",                        starter: "Community", team: "WhatsApp + email", ent: "24×7 Slack + phone" },
  { row: "SLA",                            starter: "—",       team: "4 h response", ent: "1 h P0 · 99.95% uptime" },
];

const FAQ = [
  { q: "Is Starter free forever?",
    a: "Yes — Starter is free forever for one project and up to 100 000 drillhole intervals. No credit card required. Upgrade at any point without losing data." },
  { q: "Do you charge per seat?",
    a: "Team includes 5 users at a flat rate. Enterprise includes unlimited users. We deliberately don't nickel-and-dime per seat." },
  { q: "Do you offer academic or student discounts?",
    a: "Universities and accredited training programmes get free unlimited access. Write to academic@sresources.co with your institution letterhead." },
  { q: "Can I pay by invoice / bank transfer / GST invoice?",
    a: "Yes — annual invoicing with GST/VAT-compliant billing is available on Team and Enterprise plans." },
  { q: "What happens if I exceed my data volume?",
    a: "We alert you at 80% and again at 100% — nothing gets deleted or throttled. You have 30 days to upgrade or archive older projects." },
  { q: "Can I move from Team to Enterprise later?",
    a: "Absolutely. Enterprise migrations are white-glove — we consolidate your projects, add SSO and deliver an on-prem quote if needed. Typically < 2 weeks." },
];

function Cell({ value }) {
  if (value === true)  return <Check className="h-4 w-4 mx-auto" style={{ color: "#4CAF50" }} strokeWidth={3} />;
  if (value === false) return <X     className="h-4 w-4 mx-auto text-neutral-300" strokeWidth={2} />;
  return <span className="text-neutral-700">{value}</span>;
}

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-neutral-200 last:border-b-0" data-testid={`pricing-faq-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-6 py-5 text-left"
        aria-expanded={open}
      >
        <span className="text-sm md:text-base font-semibold text-neutral-900">{q}</span>
        <ChevronDown
          className={"h-4 w-4 mt-1 shrink-0 transition-transform " + (open ? "rotate-180" : "")}
          style={{ color: "#4CAF50" }}
        />
      </button>
      {open && <div className="pb-5 pr-8 text-sm text-neutral-600 leading-relaxed">{a}</div>}
    </div>
  );
}

export default function PricingPage() {
  return (
    <div className="min-h-screen flex flex-col bg-white text-neutral-900" data-testid="page-pricing">
      <SminHeader variant="marketing" forceGuest navLinks={NAV} />

      <main className="flex-1">
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-10 text-center">
          <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#4CAF50" }}>
            Pricing
          </div>
          <h1 className="mt-3 text-3xl md:text-5xl font-bold tracking-tight max-w-3xl mx-auto leading-tight">
            Simple, transparent pricing.
          </h1>
          <p className="mt-5 max-w-2xl mx-auto text-neutral-600 text-sm md:text-base leading-relaxed">
            Start free. Scale to full-portfolio deployments. Every tier includes the same
            estimation, pit optimisation and reporting engines — you only pay for team size and data volume.
          </p>
        </section>

        {/* Tiers */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 pb-16">
          <div className="grid md:grid-cols-3 gap-6">
            {TIERS.map((t) => (
              <div
                key={t.name}
                data-testid={`pricing-tier-${t.name.toLowerCase()}`}
                className={"rounded-xl p-7 border transition flex flex-col " +
                  (t.highlight
                    ? "border-transparent shadow-xl scale-[1.02]"
                    : "border-neutral-200 hover:shadow-md")}
                style={t.highlight ? { borderColor: "#4CAF50", background: "#F6FBF6" } : {}}
              >
                {t.highlight && (
                  <div className="inline-block text-[10px] font-bold uppercase tracking-widest text-white rounded-full px-2.5 py-1 mb-4 self-start"
                       style={{ background: "#4CAF50" }}>
                    Most Popular
                  </div>
                )}
                <div className="text-lg font-bold">{t.name}</div>
                <div className="mt-4 flex items-baseline gap-1.5">
                  <span className="text-4xl font-bold tracking-tight">{t.price}</span>
                  <span className="text-sm text-neutral-500">{t.period}</span>
                </div>
                <p className="mt-2 text-sm text-neutral-600">{t.tagline}</p>
                <ul className="mt-6 space-y-2.5 text-sm flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#4CAF50" }} strokeWidth={3} />
                      <span className="text-neutral-700 leading-snug">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={t.ctaHref}
                  data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                  className={"mt-7 w-full inline-flex items-center justify-center h-11 rounded-md font-semibold text-sm transition-colors " +
                    (t.highlight ? "text-white" : "border-2")}
                  style={t.highlight
                    ? { background: "#4CAF50" }
                    : { borderColor: "#4CAF50", color: "#4CAF50" }}
                >
                  {t.cta}
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* Compare table */}
        <section className="bg-neutral-50 py-16">
          <div className="max-w-6xl mx-auto px-6 md:px-10">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#4CAF50" }}>
                Full comparison
              </div>
              <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">What's included in each plan.</h2>
            </div>

            <div className="overflow-x-auto rounded-xl border border-neutral-200 bg-white">
              <table className="w-full text-sm" data-testid="pricing-compare-table">
                <thead className="bg-neutral-100 text-neutral-600 uppercase text-[11px] tracking-widest">
                  <tr>
                    <th className="text-left px-5 py-3 font-semibold">Feature</th>
                    <th className="px-5 py-3 font-semibold text-center">Starter</th>
                    <th className="px-5 py-3 font-semibold text-center" style={{ color: "#4CAF50" }}>Team</th>
                    <th className="px-5 py-3 font-semibold text-center">Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARE.map((r, i) => (
                    <tr key={r.row} className={i % 2 ? "bg-neutral-50" : ""}>
                      <td className="px-5 py-3 font-medium text-neutral-800">{r.row}</td>
                      <td className="px-5 py-3 text-center"><Cell value={r.starter} /></td>
                      <td className="px-5 py-3 text-center"><Cell value={r.team} /></td>
                      <td className="px-5 py-3 text-center"><Cell value={r.ent} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-xs text-neutral-500 text-center">
              All prices exclude GST/VAT. Enterprise pricing quoted per commercial engagement.
            </p>
          </div>
        </section>

        {/* Pricing FAQ */}
        <section className="max-w-3xl mx-auto px-6 md:px-10 py-16">
          <div className="text-center mb-8">
            <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#4CAF50" }}>
              Pricing FAQ
            </div>
            <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">
              The questions we get most.
            </h2>
          </div>
          <div className="rounded-xl border border-neutral-200 bg-white px-6">
            {FAQ.map((it, i) => <FAQItem key={it.q} q={it.q} a={it.a} idx={i} />)}
          </div>
          <div className="mt-6 text-center text-sm text-neutral-600">
            More questions?{" "}
            <Link to="/faq" className="font-semibold underline" style={{ color: "#4CAF50" }}>
              See the full FAQ
            </Link>{" "}
            or{" "}
            <Link to="/contact" className="font-semibold underline" style={{ color: "#4CAF50" }}>
              contact us
            </Link>.
          </div>
        </section>

        {/* Enterprise CTA */}
        <section className="bg-neutral-50 py-14">
          <div className="max-w-3xl mx-auto text-center px-6">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
              Multi-asset portfolio or on-prem requirements?
            </h2>
            <p className="mt-3 text-sm text-neutral-600">
              Enterprise engagements start with a discovery call. We'll scope, quote and pilot within 4 weeks.
            </p>
            <Link
              to="/contact"
              data-testid="pricing-enterprise-cta"
              className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md text-white font-semibold text-sm"
              style={{ background: "#4CAF50" }}
            >
              Talk to Sales <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </main>

      <SminFooter />
    </div>
  );
}
